import type { AppDispatch } from '@/redux/store';
import type { LicenseByZipParamsInterface } from '@/model/explore';
import { setLicenseByState, setLicenseByZipQueryParams } from '@/explore/ducks/slice';

/**
 * GET /dealers/filter/collections may return a bare array or a wrapped payload.
 * Always normalize to an array before storing in redux or mapping in the UI.
 */
export function coerceSavedFilterCollectionsToArray(value: unknown): any[] {
  if (Array.isArray(value)) return value;
  if (value && typeof value === 'object') {
    const o = value as Record<string, unknown>;
    if (Array.isArray(o.data)) return o.data;
    if (Array.isArray(o.collections)) return o.collections;
    if (Array.isArray(o.items)) return o.items;
    if (Array.isArray(o.results)) return o.results;
  }
  return [];
}

/** First collection marked default (API: `is_default` or UI: `isDefault`). */
export function getDefaultSavedFilterCollection(collections: unknown): any | null {
  const list = coerceSavedFilterCollectionsToArray(collections);
  return (
    list.find((f: any) => f.is_default === true || f.isDefault === true) ?? null
  );
}

/** True when payload looks like ag-grid column filter map (not explore sort/filter params). */
function isAgGridStyleSearchFilters(obj: Record<string, unknown>): boolean {
  return Object.values(obj).some((v) => {
    if (!v || typeof v !== 'object' || Array.isArray(v)) return false;
    return 'filterType' in (v as Record<string, unknown>);
  });
}

/**
 * Applies a saved collection's `search_filters` to explore redux so dealer search refetches.
 * Expects the same flat shape produced by `getParamsWithState()` when saving (LicenseSortOptions + optional `state`).
 */
export function applySavedSearchFiltersToExploreState(
  dispatch: AppDispatch,
  searchFilters: unknown
): void {
  if (!searchFilters || typeof searchFilters !== 'object' || Array.isArray(searchFilters)) {
    return;
  }
  const sf = searchFilters as Record<string, unknown>;
  if (isAgGridStyleSearchFilters(sf)) {
    return;
  }
  const { state, ...rest } = sf;
  if (Array.isArray(state)) {
    dispatch(setLicenseByState(state as string[]));
  } else {
    dispatch(setLicenseByState([]));
  }
  dispatch(
    setLicenseByZipQueryParams({
      sortBy: 'preferred',
      ...(rest as LicenseByZipParamsInterface)
    })
  );
}
